import * as Sentry from '@sentry/react';

/**
 * Structured-log helpers for the WebRTC calling feature.
 *
 * Every WebRTC log carries `feature: 'video-calling'` and, while a call
 * is active, `call_id` + `unit_id`. Filter by these in the Sentry Logs
 * Explorer to pull the full timeline of a single call.
 */

type LogLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal';

interface CallContext {
  callId: string;
  unitId: number;
}

let currentCall: CallContext | null = null;

/**
 * Mark the start of a call. `call_id` and `unit_id` are included on every
 * `wlog(...)` call until `clearCurrentCall()` runs at teardown.
 */
export function setCurrentCall(context: CallContext): void {
  currentCall = context;
}

export function clearCurrentCall(): void {
  currentCall = null;
}

export function getCallId(): string | null {
  return currentCall?.callId ?? null;
}

export function generateCallId(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return `call_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Emit a structured WebRTC log. Merges the per-call attributes
 * (`call_id`, `unit_id`) and the stable `feature: 'video-calling'` tag
 * into the attribute bag so filtering in Sentry is one click.
 */
export function wlog(
  level: LogLevel,
  message: Parameters<typeof Sentry.logger.info>[0],
  attributes: Record<string, unknown> = {},
): void {
  Sentry.logger[level](message, {
    feature: 'video-calling',
    ...(currentCall && {
      call_id: currentCall.callId,
      unit_id: currentCall.unitId,
    }),
    ...attributes,
  });
}

export const fmt = Sentry.logger.fmt;

/**
 * Candidate type/protocol tally used to summarise an ICE gathering
 * cycle without logging every individual candidate.
 */
export interface CandidateCounters {
  host: number;
  srflx: number;
  prflx: number;
  relay: number;
  udp: number;
  tcp: number;
  total: number;
}

export function emptyCandidateCounters(): CandidateCounters {
  return {
    host: 0,
    srflx: 0,
    prflx: 0,
    relay: 0,
    udp: 0,
    tcp: 0,
    total: 0,
  };
}

export function tallyCandidate(
  counters: CandidateCounters,
  candidate:
    | RTCIceCandidate
    | (RTCIceCandidateInit &
        Partial<Pick<RTCIceCandidate, 'protocol' | 'type'>>),
): void {
  counters.total += 1;
  switch (candidate.type) {
    case 'host':
      counters.host += 1;
      break;
    case 'srflx':
      counters.srflx += 1;
      break;
    case 'prflx':
      counters.prflx += 1;
      break;
    case 'relay':
      counters.relay += 1;
      break;
    default:
      break;
  }
  // `RTCIceCandidate.protocol` is typed as `'udp' | 'tcp' | null`.
  // TLS is a relay-level concern exposed via getStats, not here.
  if (candidate.protocol === 'udp') counters.udp += 1;
  else if (candidate.protocol === 'tcp') counters.tcp += 1;
}

export interface SelectedPairSummary {
  local_type?: string;
  local_protocol?: string;
  local_relay_protocol?: string;
  local_tcp_type?: string;
  remote_type?: string;
  remote_protocol?: string;
  current_round_trip_time?: number;
  available_outgoing_bitrate?: number;
  bytes_sent?: number;
  bytes_received?: number;
  state?: string;
}

/**
 * Inspect `pc.getStats()` and return a compact description of the
 * nominated candidate pair. Used on the ICE `connected`/`completed`
 * transition so each successful call logs the route it landed on
 * (host / srflx / relay, udp / tcp / tls).
 */
export async function extractSelectedPair(
  pc: RTCPeerConnection,
): Promise<SelectedPairSummary | null> {
  try {
    const stats = await pc.getStats();
    let selectedPairId: string | undefined;
    let selectedPair: Record<string, unknown> | undefined;

    stats.forEach((report) => {
      const r = report as unknown as Record<string, unknown>;
      if (
        r.type === 'transport' &&
        typeof r.selectedCandidatePairId === 'string'
      ) {
        selectedPairId = r.selectedCandidatePairId;
      }
    });

    if (selectedPairId) {
      const pair = stats.get(selectedPairId);
      if (pair) selectedPair = pair as unknown as Record<string, unknown>;
    }

    if (!selectedPair) {
      stats.forEach((report) => {
        if (selectedPair) return;
        const r = report as unknown as Record<string, unknown>;
        if (r.type !== 'candidate-pair') return;
        if ((r.nominated && r.selected) || r.state === 'succeeded') {
          selectedPair = r;
        }
      });
    }

    if (!selectedPair) return null;

    const localId = selectedPair.localCandidateId as string | undefined;
    const remoteId = selectedPair.remoteCandidateId as string | undefined;
    const local = localId
      ? (stats.get(localId) as unknown as Record<string, unknown> | undefined)
      : undefined;
    const remote = remoteId
      ? (stats.get(remoteId) as unknown as Record<string, unknown> | undefined)
      : undefined;

    return {
      local_type: local?.candidateType as string | undefined,
      local_protocol: local?.protocol as string | undefined,
      local_relay_protocol: local?.relayProtocol as string | undefined,
      local_tcp_type: local?.tcpType as string | undefined,
      remote_type: remote?.candidateType as string | undefined,
      remote_protocol: remote?.protocol as string | undefined,
      current_round_trip_time: selectedPair.currentRoundTripTime as
        | number
        | undefined,
      available_outgoing_bitrate: selectedPair.availableOutgoingBitrate as
        | number
        | undefined,
      bytes_sent: selectedPair.bytesSent as number | undefined,
      bytes_received: selectedPair.bytesReceived as number | undefined,
      state: selectedPair.state as string | undefined,
    };
  } catch {
    return null;
  }
}

export interface AttemptedPairSummary {
  state?: string;
  nominated?: boolean;
  local_type?: string;
  local_protocol?: string;
  remote_type?: string;
  remote_protocol?: string;
  priority?: number;
  bytes_sent?: number;
}

/**
 * On terminal ICE failure, dump every candidate-pair's state so we can
 * answer "which routes did we try and why did each one fail?". States
 * are the ICE transport-level `waiting | in-progress | succeeded |
 * failed | frozen`.
 */
export async function extractAttemptedPairs(
  pc: RTCPeerConnection,
): Promise<AttemptedPairSummary[]> {
  try {
    const stats = await pc.getStats();
    const candidateMap = new Map<string, Record<string, unknown>>();
    stats.forEach((report) => {
      const r = report as unknown as Record<string, unknown>;
      if (r.type === 'local-candidate' || r.type === 'remote-candidate') {
        candidateMap.set(r.id as string, r);
      }
    });

    const pairs: AttemptedPairSummary[] = [];
    stats.forEach((report) => {
      const r = report as unknown as Record<string, unknown>;
      if (r.type !== 'candidate-pair') return;
      const local = r.localCandidateId
        ? candidateMap.get(r.localCandidateId as string)
        : undefined;
      const remote = r.remoteCandidateId
        ? candidateMap.get(r.remoteCandidateId as string)
        : undefined;
      pairs.push({
        state: r.state as string | undefined,
        nominated: r.nominated as boolean | undefined,
        local_type: local?.candidateType as string | undefined,
        local_protocol: local?.protocol as string | undefined,
        remote_type: remote?.candidateType as string | undefined,
        remote_protocol: remote?.protocol as string | undefined,
        priority: r.priority as number | undefined,
        bytes_sent: r.bytesSent as number | undefined,
      });
    });
    return pairs;
  } catch {
    return [];
  }
}
